import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import usePopTransition from './usePopTransition';

/**
 * A drop-in for <select> whose list is drawn by the page, so it opens with the suite's motion
 * and matches the theme instead of the operating system's popup.
 *
 * Same shape as a native select: <option> (and <optgroup>) children, `value`, and an `onChange`
 * that receives `{ target: { value } }` with the value as a string, so an existing handler
 * reading `e.target.value` keeps working. Converting one is renaming the tag.
 *
 * On a touch screen it renders the native <select>, whose full-screen picker suits a phone
 * better than any list drawn in the page.
 *
 * The closed button is as wide as the longest option label whichever option is selected (a hidden,
 * zero-height copy of every label shares the shown label's grid cell), and the open list is exactly
 * the button's width. A button given a fixed width by its own class keeps it and trims a longer
 * label with an ellipsis.
 *
 * A long list can be searched (Ivan, 2026-11-04: 49 agents and no way to type): from SEARCH_FROM options up (or with `searchable`), the field itself becomes the
 * search box while the list is open, the selected label as its placeholder, and what is typed filters the list under it. Every word typed has to be found in an
 * option's label, those starting with the first word first; Enter takes the highlighted one and Escape closes. A shorter list stays a plain pick.
 *
 * Its stylesheet (AppSelect.css) is imported once in App.js: imported from here, it would land in
 * several lazily loaded chunks in different orders, which the production build rejects.
 */

const coarsePointer = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

/** Flatten <option> / <optgroup> children (through arrays and fragments) into a list. */
function readOptions(children) {
  const out = [];
  const walk = (nodes, group) => {
    React.Children.forEach(nodes, (node) => {
      if (!React.isValidElement(node)) return;
      if (node.type === React.Fragment) {
        walk(node.props.children, group);
      } else if (node.type === 'optgroup') {
        out.push({ header: true, label: node.props.label, key: `g-${out.length}` });
        walk(node.props.children, node.props.label);
      } else if (node.type === 'option') {
        const label = node.props.children;
        const value = node.props.value !== undefined ? String(node.props.value) : String(label ?? '');
        out.push({ value, label, disabled: !!node.props.disabled, hidden: !!node.props.hidden, group, key: `o-${value}-${out.length}` });
      }
    });
  };
  walk(children);
  return out;
}

// Above this many options the button is not sized by a hidden copy of every label.
const SIZER_LIMIT = 60;
// From this many options up a list can be searched by typing in its field.
export const SEARCH_FROM = 8;

const norm = (v) => String(v == null ? '' : v).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

function labelText(label) {
  if (label == null || typeof label === 'boolean') return '';
  if (typeof label === 'string' || typeof label === 'number') return String(label);
  if (Array.isArray(label)) return label.map(labelText).join('');
  if (React.isValidElement(label)) return labelText(label.props.children);
  return '';
}

export default function AppSelect({
  value,
  onChange,
  children,
  className = '',
  disabled = false,
  id,
  title,
  style,
  name,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  searchable
}) {
  const autoId = useId().replace(/:/g, '');
  const options = useMemo(() => readOptions(children), [children]);
  const choices = options.filter((o) => !o.header && !o.hidden);
  const current = value == null ? '' : String(value);
  const selected = choices.find((o) => o.value === current) || null;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [place, setPlace] = useState(null);
  const [query, setQuery] = useState('');
  const canSearch = searchable === undefined ? choices.length >= SEARCH_FROM : Boolean(searchable);
  const refocus = useRef(false);
  const inputRef = useRef(null);
  // What the list shows: every option, or (while a search is typed) the ones holding every typed word, those starting with the first word first.
  const matching = useMemo(() => {
    const words = canSearch ? norm(query).split(/\s+/).filter(Boolean) : [];
    if (!words.length) return choices;
    return choices
      .filter((o) => { const t = norm(labelText(o.label)); return words.every((w) => t.includes(w)); })
      .map((o, i) => ({ o, i, first: norm(labelText(o.label)).startsWith(words[0]) ? 0 : 1 }))
      .sort((a, b) => a.first - b.first || a.i - b.i)
      .map((r) => r.o);
  }, [choices, query, canSearch]);
  const pop = usePopTransition(open);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const typed = useRef({ text: '', at: 0 });
  const [native] = useState(coarsePointer);

  const emit = useCallback((next) => {
    if (next === current) return;
    if (onChange) onChange({ target: { value: next, name }, currentTarget: { value: next, name } });
  }, [current, onChange, name]);

  const close = useCallback((focusButton = true) => {
    setOpen(false);
    setQuery('');
    refocus.current = focusButton; // the button (not the box that stood in for it) takes the focus back once it is drawn again
    if (focusButton && buttonRef.current && buttonRef.current.focus) buttonRef.current.focus();
  }, []);
  useEffect(() => {
    if (!open && refocus.current) { refocus.current = false; if (buttonRef.current && buttonRef.current.focus) buttonRef.current.focus(); }
  }, [open]);

  const openList = useCallback(() => {
    if (disabled) return;
    const idx = choices.findIndex((o) => o.value === current);
    setQuery('');
    setActive(idx >= 0 ? idx : choices.findIndex((o) => !o.disabled));
    setOpen(true);
  }, [choices, current, disabled]);
  // A searchable field is an input while it is open: the cursor goes into it.
  useEffect(() => { if (open && canSearch && inputRef.current) inputRef.current.focus(); }, [open, canSearch]);

  // Place the list under the button (above it when there is no room), in viewport coordinates:
  // it is portalled to <body>, so a table cell's overflow cannot clip it.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const up = below < 220 && r.top > below;
    setPlace({
      left: Math.min(r.left, window.innerWidth - Math.max(r.width, 160) - 8),
      width: r.width,
      maxHeight: Math.max(160, Math.min(320, (up ? r.top : below) - 12)),
      ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      up
    });
  }, [open]);

  // Outside click, scroll or resize closes it, as a native select does.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (buttonRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      close(false);
    };
    const onScroll = (e) => {
      if (listRef.current && listRef.current.contains(e.target)) return;
      close(false);
    };
    const onResize = () => close(false);
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  // Keep the highlighted option in view.
  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const step = (from, dir) => {
    for (let i = from + dir; i >= 0 && i < matching.length; i += dir) {
      if (!matching[i].disabled) return i;
    }
    return from;
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') { close(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => step(a, 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => step(a, -1)); return; }
    if (e.key === 'Home' && !canSearch) { e.preventDefault(); setActive(step(-1, 1)); return; }
    if (e.key === 'End' && !canSearch) { e.preventDefault(); setActive(step(matching.length, -1)); return; }
    if (e.key === 'Enter' || (e.key === ' ' && !canSearch)) {
      e.preventDefault();
      const o = matching[active];
      if (o && !o.disabled) emit(o.value);
      close();
      return;
    }
    // Type-ahead: letters jump to the first option starting with what was typed (a searchable field filters instead).
    if (!canSearch && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLowerCase(), at: now };
      const hit = choices.findIndex((o) => !o.disabled && labelText(o.label).toLowerCase().startsWith(typed.current.text));
      if (hit >= 0) setActive(hit);
    }
  };

  if (native) {
    return (
      <select
        id={id}
        name={name}
        className={className}
        style={style}
        title={title}
        value={current}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onChange={(e) => emit(e.target.value)}
      >
        {children}
      </select>
    );
  }

  const listId = id ? `${id}-list` : `app-select-${autoId}`;

  const searching = open && canSearch;
  const shownLabel = selected ? selected.label : (choices[0]?.label ?? ' ');
  const matchKeys = new Set(matching.map((o) => o.key));
  const groupsWithMatch = new Set(matching.map((o) => o.group));

  return (
    <>
      {searching ? (
        // The field while its list is open: the same box (same classes, same width), with the cursor in it.
        <div
          ref={buttonRef}
          className={`app-select-button app-select-button--searching ${className}`.trim()}
          style={style}
          title={title}
        >
          <input
            ref={inputRef}
            type="text"
            id={id}
            className="app-select-search"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={listId && active >= 0 ? `${listId}-o${active}` : undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            placeholder={labelText(shownLabel).trim() || 'Search'}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          id={id}
          className={`app-select-button ${className}`.trim()}
          style={style}
          title={title}
          disabled={disabled}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          onClick={() => (open ? close() : openList())}
          onKeyDown={onKeyDown}
        >
          <span className="app-select-button__label">
            <span className="app-select-button__shown">{shownLabel}</span>
            {choices.length <= SIZER_LIMIT && choices.map((o) => (
              <span key={o.key} className="app-select-button__sizer" aria-hidden="true">{o.label === '' || o.label == null ? ' ' : o.label}</span>
            ))}
          </span>
        </button>
      )}
      {pop.mounted && place && createPortal(
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className={`app-select-list ui-pop${place.up ? ' ui-pop--up' : ''}${pop.closing ? ' is-closing' : ''}`}
          style={{
            left: place.left,
            width: place.width,
            maxHeight: place.maxHeight,
            ...(place.up ? { bottom: place.bottom } : { top: place.top })
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map((o) => {
            if (o.header) return !query.trim() || groupsWithMatch.has(o.label) ? <div key={o.key} className="app-select-list__group">{o.label}</div> : null;
            if (o.hidden) return null;
            if (!matchKeys.has(o.key)) return null;
            const idx = matching.indexOf(o);
            const isSelected = o.value === current;
            return (
              <div
                key={o.key}
                id={`${listId}-o${idx}`}
                role="option"
                data-index={idx}
                aria-selected={isSelected}
                aria-disabled={o.disabled || undefined}
                className={`app-select-list__option${isSelected ? ' is-selected' : ''}${idx === active ? ' is-active' : ''}${o.disabled ? ' is-disabled' : ''}`}
                onMouseEnter={() => setActive(idx)}
                onClick={() => {
                  if (o.disabled) return;
                  emit(o.value);
                  close();
                }}
              >
                {o.label === '' || o.label == null ? ' ' : o.label}
              </div>
            );
          })}
          {canSearch && query.trim() && !matching.length ? <div className="app-select-list__empty">Nothing matches &ldquo;{query.trim()}&rdquo;.</div> : null}
        </div>,
        document.body
      )}
    </>
  );
}
